import { IRoomResource } from 'kits/apiKit3/legacy';
import { sendEvent } from 'kits/eventKit';
import IvicosStrings from 'kits/language/stringKit';
import { useNavigate } from 'react-router';
import { TEventHandler, TUpdateEvent, TUserListFromServer } from 'services/socket-connection/types';
import { useRoomList } from 'shared-state/directory/hooks';
import { useLocalProfile, useSetOtherUsersStatusMap, useSetOtherUsersStatusFromServer } from 'shared-state/identity/hooks';
import { isSocketStatusFresh, markSocketStatusUpdate, putStoredStatus } from 'shared-state/identity/statusPersistence';
import { makeMessageId, useReceivedMessageInbox } from 'shared-state/notifications/hooks';
import { IReceivedMessage } from 'shared-state/notifications/types';
import { useDirectMessageUserList, useUserList, useUserListForRoomSelection, useUsersAndVisitorsNumber, useVisitorList } from 'shared-state/presence/hooks';
import {
    SERVER_EMIT_CROSS_AREA_MESSAGE_USER_LIST,
    SERVER_EMIT_HOST_USER_AT_VISITOR_ENTRANCE,
    SERVER_EMIT_KICK_VISITOR,
    SERVER_EMIT_MESSAGE_EVENT,
    SERVER_EMIT_ROOM_EVENT,
    SERVER_EMIT_ROOM_SESSION_TOKEN,
    SERVER_EMIT_USER_AT_VISITOR_ENTRANCE,
    SERVER_EMIT_USER_JOINED,
    SERVER_EMIT_USER_JOINED_AN_AREA,
    SERVER_EMIT_USER_LEFT_OR_LOGGED_OUT,
    SERVER_EMIT_VISITOR_JOINED,
    SERVER_EMIT_NUMBER_OF_USERS_AND_VISITORS_IN_AREAS,
    SERVER_EMIT_RELOAD_PAGE,
    SERVER_EMIT_USERS_LOCATION_IN_AREA_FOR_HOST,
    SERVER_EMIT_MESSAGE_AT_VISITOR_ENTRANCE,
    SERVER_EMIT_REDIRECT_USER_TO_PERSONAL_ROOM,
    SERVER_EMIT_REDIRECT_USER_TO_PERSONAL_ROOM_CONFIRMATION,
    SERVER_EMIT_USER_CURRENT_STATUS
} from '../../events';
import { useJitsiSessionIdHandler } from '../../hooks';
import { idpService } from 'services/api/identity-provider.service';
import { preserveMatrixIdentityAcrossClear } from 'services/matrix-connection/matrixClient';
import { useCurrentCampusId } from 'shared-state/location/hook';
import { useRef } from 'react';
import { useEmittingEvents } from '../event-actions/emittingEvents';
import { redirectionInformationModal, showConfirmationDialog } from 'features/Rooms/RoomList/helpers/userHooks/redirectionModal/redirectionModal';

const STATUS_FETCH_DELAY_MS = 500;
const CROSS_AREA_STATUS_FETCH_DELAY_MS = 700;

const buildStatusMap = (statusList: any[]) => {
    const statusInfoMap: Record<string, any> = {};
    for (const status of statusList) {
        const userId = status?.userId ?? status?.uid;
        if (userId) {
            statusInfoMap[userId] = { ...status, userId, version: 1 };
        }
    }
    return statusInfoMap;
};

const syncOtherUsersStatusMapFromApi = (setOtherUsersStatusMap: any, statusList: any[]) => {
    const fromApi = buildStatusMap(statusList);
    setOtherUsersStatusMap((prev: Record<string, any>) => {
        const merged = { ...prev };
        let changed = false;

        for (const [userId, apiStatus] of Object.entries(fromApi)) {
            if (isSocketStatusFresh(userId)) continue;
            if (JSON.stringify(prev[userId]) === JSON.stringify(apiStatus)) continue;
            merged[userId] = apiStatus;
            changed = true;
        }

        return changed ? merged : prev;
    });
};

const fetchPersistAndSyncOtherUsersStatus = async (
    users: { uid: string }[],
    setOtherUsersStatusFromServer: any,
    setOtherUsersStatusMap: any,
    successLog: string
) => {
    const userStatusInfo = await Promise.all(users.map((user) => idpService.getUserStatusInfo(user.uid)));
    for (const user of userStatusInfo) {
        const userId = user?.userId ?? user?.uid;
        if (!userId || isSocketStatusFresh(userId)) continue;
        await putStoredStatus({
            ...user,
            userId,
            version: 1
        });
    }
    setOtherUsersStatusFromServer(userStatusInfo);
    syncOtherUsersStatusMapFromApi(setOtherUsersStatusMap, userStatusInfo);
    console.log(successLog);
};

const scheduleStatusFetch = (
    timerRef: { current: ReturnType<typeof setTimeout> | null },
    delayMs: number,
    users: { uid: string }[],
    setOtherUsersStatusFromServer: any,
    setOtherUsersStatusMap: any,
    successLog: string,
    errorLog: string
) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
        timerRef.current = null;
        try {
            await fetchPersistAndSyncOtherUsersStatus(users, setOtherUsersStatusFromServer, setOtherUsersStatusMap, successLog);
        } catch (err) {
            console.warn(errorLog, err);
        }
    }, delayMs);
};

const useUserListUpdateHandler = () => {
    const [, setUserList] = useUserList();
    const localProfile = useLocalProfile();
    const [campusId] = useCurrentCampusId();
    const [, setOtherUsersStatusFromServer] = useSetOtherUsersStatusFromServer();
    const setOtherUsersStatusMap = useSetOtherUsersStatusMap();
    const fetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const VISITOR_TYPES = ['visitor', 'auto-confirmed-visitor'];

    // 🧠 In-memory cache that clears on reload
    const visitorCacheRef = useRef<{ visitors: any[]; lastUIDsHash: string } | null>(null);

    const getLocalStorageItem = (key: string) => localStorage.getItem(key);

    const excludeCurrentUserAndFilterByArea = (users: TUserListFromServer[]) => {
        const lastAreaId = getLocalStorageItem('lastAreaId');
        const localID = getLocalStorageItem('localID');

        return users.filter(({ uid, userInThisArea }) => uid !== localID && userInThisArea === lastAreaId);
    };

    // 🔑 Utility: Create a simple hash from sorted UID list
    const createUIDHash = (users: TUserListFromServer[]) => {
        return users
            .map((u) => u.uid)
            .sort()
            .join(',');
    };

    const getVisitorsListWithCaching = async (serverList: TUserListFromServer[]): Promise<any[]> => {
        const currentUIDHash = createUIDHash(serverList);

        // 🧠 Use cache if hash matches
        if (visitorCacheRef.current?.lastUIDsHash === currentUIDHash) {
            return visitorCacheRef.current.visitors;
        }

        // Fetch fresh and cache
        try {
            const { visitors } = await idpService.getVisitorsList(campusId);
            visitorCacheRef.current = {
                visitors,
                lastUIDsHash: currentUIDHash
            };
            return visitors;
        } catch (err) {
            console.error('❌ Error fetching visitors list:', err);
            return [];
        }
    };

    const crossCheckVisitorListWithDB = async (users: TUserListFromServer[]) => {
        const visitors = await getVisitorsListWithCaching(users);
        const visitorSet = new Set(visitors.map((v) => v.uid));
        const localID = getLocalStorageItem('localID');

        const result = users.filter((user) => {
            // If not a visitor type, keep them
            if (!VISITOR_TYPES.includes(user.type)) {
                return true;
            }

            // Get stored visitor info
            const storedVisitorInfo = sessionStorage.getItem('visitorIdentity');
            const visitorIdentity = storedVisitorInfo ? JSON.parse(storedVisitorInfo) : null;

            if (!visitorIdentity && user.uid === localID && VISITOR_TYPES.includes(user.type)) {
                preserveMatrixIdentityAcrossClear(() => localStorage.clear());
                sessionStorage.clear();
                window.location.href = '/login';
                return false;
            }

            // If visitor type, check if they exist in DB
            const isVisitorInDB = visitorSet.has(user.uid);

            if (user.uid === localID && visitorIdentity) {
                // Verify stored identity matches current user
                const isStoredVisitorValid =
                    visitorIdentity.uid === user.uid && visitorIdentity.campusId === user.campusId && visitorIdentity.areaId === user.userInThisArea;

                if (!isStoredVisitorValid || !isVisitorInDB) {
                    preserveMatrixIdentityAcrossClear(() => localStorage.clear());
                    sessionStorage.clear();
                    window.location.href = '/login';
                    return false;
                }
            }

            return isVisitorInDB;
        });

        return excludeCurrentUserAndFilterByArea(result);
    };

    const handleUserListUpdate = async (userListFromServer: TUserListFromServer[]) => {
        //! Note: userListFromServer contains only users who are currently inside an area
        if (!Array.isArray(userListFromServer)) {
            console.error('❌ Invalid user list from server:', typeof userListFromServer, JSON.stringify(userListFromServer).substring(0, 200));
            return;
        }

        const excludeUsersFromOtherCampuses = userListFromServer.filter((user) => user.campusId === localProfile?.campusId);

        // this is done to use for status fetch
        const excludeCurrentUserForStatusFetch = excludeUsersFromOtherCampuses.filter((user) => user.uid !== localProfile?.uid);
        console.log('📡 connected to the websocket server 🎉', excludeUsersFromOtherCampuses);

        const containsVisitor = excludeUsersFromOtherCampuses.some((user) => VISITOR_TYPES.includes(user.type));

        if (containsVisitor) {
            const result = await crossCheckVisitorListWithDB(excludeUsersFromOtherCampuses);
            setUserList(result);

            // Fetch and persist other users status on initial list update
            // Small delay to avoid race condition with originating user's backend persistence
            /**
             * Status Fetch Strategy Documentation:
             *
             * - We utilize 'excludeCurrentUserForStatusFetch' instead of 'result' when fetching user statuses.
             *   - Why? 'result' contains only those users currently inside this specific area (after filtering via excludeCurrentUserAndFilterByArea).
             *   - 'excludeCurrentUserForStatusFetch' is a broader list, including users across all areas (except the current user) within the campus.
             *   - Fetching status for this expanded set of users ensures we obtain up-to-date status for users both in this area and other areas.
             *
             * - This approach parallels the way 'handleCrossAreaMessageUserListsHandler' operates: it fetches statuses not only for users visible in the current list
             *   but also for those present in the lobby.
             *
             * - In summary: Use 'excludeCurrentUserForStatusFetch' to perform a comprehensive status update for all online and relevant users,
             *   not just those filtered by the current area.
             */

            scheduleStatusFetch(
                fetchTimerRef,
                STATUS_FETCH_DELAY_MS,
                excludeCurrentUserForStatusFetch,
                setOtherUsersStatusFromServer,
                setOtherUsersStatusMap,
                '✅ Initial status fetch completed @ userListUpdateHandler',
                '⚠️ Failed to fetch initial status info:'
            );
        } else {
            const filteredList = excludeCurrentUserAndFilterByArea(excludeUsersFromOtherCampuses);
            setUserList(filteredList);

            // Fetch and persist other users' status on initial list update
            // Small delay to avoid race condition with originating user's backend persistence
            scheduleStatusFetch(
                fetchTimerRef,
                STATUS_FETCH_DELAY_MS,
                excludeCurrentUserForStatusFetch,
                setOtherUsersStatusFromServer,
                setOtherUsersStatusMap,
                '✅ Initial status fetch completed @ userListUpdateHandler',
                '⚠️ Failed to fetch initial status info:'
            );
        }
    };
    return handleUserListUpdate;
};

const useRoomListUpdateHandler = () => {
    const [, setRoomList] = useRoomList();
    const handleRoomListUpdate = (roomDataFromServer: TUpdateEvent) => {
        // console.log('room update', roomDataFromServer);
        const { type, room, roomId } = roomDataFromServer;

        if (type == 'update' && room) {
            setRoomList((prevList: IRoomResource[]) => {
                return prevList.map((r) => {
                    if (r.id != room?.id) return r;
                    // console.log('Found room, replacing...');
                    return room;
                });
            });
        }

        if (type == 'creation' && room) {
            setRoomList((prevList: IRoomResource[]) => {
                if (!room) return prevList;
                return [...prevList, ...[room]];
            });
        }

        setRoomList((prevList: IRoomResource[]) => prevList.filter((r) => r.id != roomId));
    };

    return handleRoomListUpdate;
};

const useMessageHandler = () => {
    const { handleSessionIdRequest, handleSessionIdResponse } = useJitsiSessionIdHandler();
    const [, addMessage] = useReceivedMessageInbox();
    const handleMessage = (data: any) => {
        const { type, sender, meta, body, announce } = data;
        const newReceivedMessage: IReceivedMessage = {
            id: makeMessageId(),
            sender: sender.uid,
            type: type,
            timestamp: new Date(),
            meta: {
                forRoom: meta?.forRoom,
                forUser: meta?.forUser,
                attachedRoom: meta?.attachedRoom
            },
            body: body,
            announce: announce,
            timeout: undefined,
            identityValue:
                type === 'knock'
                    ? meta?.forRoom
                        ? meta?.forRoom + '-' + sender.uid.split('-')[0]
                        : undefined
                    : meta?.attachedRoom
                    ? meta?.attachedRoom + '-' + sender.uid.split('-')[0]
                    : undefined
        };

        const crossAreaInviteMessage: IReceivedMessage = {
            id: makeMessageId(),
            sender: sender.uid,
            type: type,
            timestamp: new Date(),
            meta: {
                forRoom: meta?.forRoom,
                forUser: meta?.forUser,
                attachedRoom: meta?.attachedRoom
            },
            body: body,
            room: data.room,
            announce: announce,
            timeout: undefined,
            identityValue: meta?.attachedRoom + '-' + sender.uid.split('-')[0]
        };

        if (type === 'cross-area-invite') {
            addMessage(crossAreaInviteMessage);
        } else if (
            type != 'request-jitsisessionid' &&
            type != 'response-jitsisessionid' &&
            type !== 'round-table-update' &&
            type !== 'room-attachments-updated' &&
            type !== 'cross-area-invite'
        ) {
            addMessage(newReceivedMessage);
        } else if (type == 'request-jitsisessionid') {
            handleSessionIdRequest(sender.uid, body);
        } else if (type == 'response-jitsisessionid') {
            handleSessionIdResponse(body);
        } else if (type === 'round-table-update') {
            sendEvent('round-table-update', body);
        } else if (type === 'room-attachments-updated') {
            sendEvent('room-attachments-updated', {});
        }
    };

    return handleMessage;
};

const useVisitorJoinedHandler = () => {
    const localProfile = useLocalProfile();
    const [, addMessage] = useReceivedMessageInbox();
    const [, setVisitorList] = useVisitorList();

    const handleVisitorJoined = (data: any) => {
        console.log('visitor connected to the websocket server 🎉', data);

        const { visitor, hostsAtVisitorEntrance, isVisitorPickedUp } = data ?? {};
        const { displayName, host, subInfo } = visitor ?? {};

        if (localProfile?.uid && host && host.includes(localProfile?.uid) && !window.location.pathname.includes(`/meet/visitor-${subInfo.split(':')[2]}`))
            addMessage({
                type: 'guest',
                body: (() => {
                    const [firstHost] = hostsAtVisitorEntrance;
                    if (hostsAtVisitorEntrance.length === 1) {
                        return `${firstHost.displayName} ${IvicosStrings.visitorIsWith} ${displayName}`;
                    }
                    if (hostsAtVisitorEntrance.length > 1) {
                        return `${firstHost.displayName} ${IvicosStrings.visitorWithOthers} ${displayName}`;
                    }
                    return `${displayName} ${IvicosStrings.visitorIsWaitingNotificationTitle}`;
                })(),
                timestamp: new Date(),
                id: makeMessageId(),
                sender: 'system',
                meta: { attachedRoom: 'visitor-' + subInfo.split(':')[2] },
                announce: isVisitorPickedUp ? false : hostsAtVisitorEntrance.length > 0 ? false : window.location.pathname.includes('/meet') ? false : true,
                hostsOfGuest: hostsAtVisitorEntrance.map((host) => ({
                    uid: host.uid,
                    name: host.displayName,
                    image: host.profileImage
                })),
                identityValue: 'visitor-' + subInfo.split(':')[2],
                dismissed: isVisitorPickedUp ? true : false
            });

        handleListUpdate(setVisitorList, visitor);
    };
    return handleVisitorJoined;
};

const useVisitorKickedOutHandler = () => {
    const localProfile = useLocalProfile();
    const routeHistory = useNavigate();

    const handleVisitorKickedOut = async (data: string) => {
        if (data === 'kick-visitor' && localProfile?.type.includes('visitor')) {
            const response = await idpService.deleteVisitor(localProfile?.uid);
            if (response.message === 'Visitor deleted successfully') {
                preserveMatrixIdentityAcrossClear(() => localStorage.clear());
                sessionStorage.clear();
                routeHistory('/e/' + localProfile?.invitationId);
            }
        }
    };

    return handleVisitorKickedOut;
};

const useVisitorListHandler = () => {
    const [, setVisitorList] = useVisitorList();
    const handleVisitorLists = (data: any) => {
        handleListUpdate(setVisitorList, data);
    };

    return handleVisitorLists;
};

const useUserListForUserPresentAtVisitorEntranceHandler = () => {
    const [userList, setUserList] = useUserList();
    const handleUserListForUserPresentAtVisitorEntrance = (data: any) => {
        const excludeUserPresentAtVisitorEntrance = userList.filter((user) => user.uid !== data.uid);
        setUserList(excludeUserPresentAtVisitorEntrance);
    };

    return handleUserListForUserPresentAtVisitorEntrance;
};

const useCrossAreaMessageUserListsHandler = () => {
    const localProfile = useLocalProfile();
    const [, setDirectMessageUserList] = useDirectMessageUserList();
    const [, setOtherUsersStatusFromServer] = useSetOtherUsersStatusFromServer();
    const setOtherUsersStatusMap = useSetOtherUsersStatusMap();
    const crossAreaFetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleCrossAreaMessageUserLists = async (data: any) => {
        const excludeUsersFromOtherCampuses = data.filter((user) => user.campusId === localProfile?.campusId);
        const excludeCurrentUserForStatusFetch = excludeUsersFromOtherCampuses.filter((user) => user.uid !== localProfile?.uid);

        scheduleStatusFetch(
            crossAreaFetchTimerRef,
            CROSS_AREA_STATUS_FETCH_DELAY_MS,
            excludeCurrentUserForStatusFetch,
            setOtherUsersStatusFromServer,
            setOtherUsersStatusMap,
            '✅ Initial status fetch completed @ handleCrossAreaMessageUserListsHandler',
            '⚠️ Failed to fetch cross-area user status info:'
        );

        const outOfOfficeUsers = await idpService.showOutOfOfficeMembers();

        /**
         * Filter out-of-office users to include in search results.
         *
         * Exclude users if:
         * - They are already in the excludeUsersFromOtherCampuses list (already visible)
         * - Their returnDate is today or in the past (should not appear in search)
         *
         * Include users only if their returnDate is in the future (tomorrow or later).
         */
        const excludeUserIds = new Set(excludeUsersFromOtherCampuses.map((user) => user.uid));
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const filteredOutOfOfficeUsers =
            outOfOfficeUsers.length > 0
                ? outOfOfficeUsers.filter((user) => {
                      if (excludeUserIds.has(user.userId)) return false;

                      // Check if returnDate is in the future (not today)
                      if (user.user_status?.returnDate) {
                          const returnDate = new Date(user.user_status.returnDate);
                          returnDate.setHours(0, 0, 0, 0);
                          return returnDate > today;
                      }
                      return false;
                  })
                : [];

        if (filteredOutOfOfficeUsers.length > 0) {
            const filteredOutOfOfficeUsersList = filteredOutOfOfficeUsers.map((user) => ({
                uid: user.userId,
                displayName: user.name,
                type: user.role,
                profileImage: user.profile_pic,
                socketId: 'socketId',
                campusId: localProfile?.campusId,
                userInThisArea: undefined,
                user_status: user.user_status
            }));
            setDirectMessageUserList([...excludeUsersFromOtherCampuses, ...filteredOutOfOfficeUsersList]);
        } else {
            setDirectMessageUserList(excludeUsersFromOtherCampuses);
        }
    };

    return handleCrossAreaMessageUserLists;
};

const updateList = (prevList: any, data: any) => {
    const existingItem = prevList.find((item: any) => item.uid === data.uid);
    if (existingItem) {
        return prevList;
    } else {
        return [...prevList, data];
    }
};

const handleListUpdate = (setList: any, data: any) => {
    setList((prevList: any) => updateList(prevList, data));
};

const useRoomSessionTokenHandler = () => {
    const routeHistory = useNavigate();
    const handleRoomSessionToken = (data: any) => {
        const { areaId, roomId, sessionToken } = data;
        sessionStorage.setItem('tempRoomSessionToken', sessionToken);
        if (sessionToken) {
            const roomPath = `/areas/${areaId}/rooms/${roomId}`;
            routeHistory(roomPath);
        }
    };

    return handleRoomSessionToken;
};

const useMessageAtVisitorEntranceHandler = () => {
    const [, addMessage] = useReceivedMessageInbox();
    const handleMessageAtVisitorEntrance = (data: any) => {
        const { type, sender, body, announce } = data;
        const newReceivedMessage: IReceivedMessage = {
            id: makeMessageId(),
            sender: sender.uid,
            type: type,
            timestamp: new Date(),
            meta: {},
            body: body,
            announce: announce,
            timeout: undefined,
            senderInfo: sender
        };
        // console.log('🚀 ~ file: users.tsx:251 ~ handleMessageAtVisitorEntrance ~ data:', data, newReceivedMessage);
        addMessage(newReceivedMessage);
    };

    return handleMessageAtVisitorEntrance;
};

const useNumberOfUsersAndVisitorsInAreasHandler = () => {
    const [, setUsersAndVisitorsNumber] = useUsersAndVisitorsNumber();
    const [campusId] = useCurrentCampusId();
    const handleNumberOfUsersAndVisitorsInAreas = async (data: any) => {
        let dbVisitors = [];
        try {
            const response = await idpService.getVisitorsList(campusId);
            dbVisitors = response.visitors;
        } catch (error: unknown) {
            if ((error as { response?: { status: number } })?.response?.status === 404) {
                dbVisitors = [];
            } else {
                throw error;
            }
        }
        const dbVisitorIds = new Set(dbVisitors.map((v: { uid: string }) => v.uid));

        // Transform data to match expected format immediately
        const transformedData = Object.entries(data).reduce((acc, [areaId, areaData]: [string, any]) => {
            const validVisitorIds = areaData.visitors.ids.filter((id) => dbVisitorIds.has(id));

            return {
                ...acc,
                [areaId]: {
                    users: areaData.users.count,
                    visitors: validVisitorIds.length
                }
            };
        }, {} as Record<string, { users: number; visitors: number }>);

        setUsersAndVisitorsNumber(transformedData);
    };

    return handleNumberOfUsersAndVisitorsInAreas;
};

const useUsersLocationInAreaForHostHandler = () => {
    const [, setUserListForRoomSelection] = useUserListForRoomSelection();
    const handleUsersLocationInAreaForHost = (data: any) => {
        setUserListForRoomSelection(data);
    };

    return handleUsersLocationInAreaForHost;
};

const useReloadPageHandler = () => {
    const handleReloadPage = () => {
        // hard reload the page
        window.location.replace(window.location.href);
    };

    return handleReloadPage;
};

const useRedirectUserToPersonalRoomHandler = () => {
    const localProfile = useLocalProfile();
    const routeHistory = useNavigate();
    const { redirectUserToPersonalRoomConfirmation } = useEmittingEvents();
    const handleRedirectUserToPersonalRoom = (data: any) => {
        const { type, areaId, receiverId, senderInfo } = data;
        if (type === 'redirect-user-to-personal-room' && localProfile && localProfile.uid === receiverId) {
            routeHistory(`/areas/${areaId}`);
            setTimeout(() => {
                redirectionInformationModal(senderInfo.name);
                redirectUserToPersonalRoomConfirmation(senderInfo.uid);
            }, 500);
        }
    };

    return handleRedirectUserToPersonalRoom;
};

const useRedirectUserToPersonalRoomConfirmationHandler = () => {
    const handleRedirectUserToPersonalRoomConfirmation = (data: any) => {
        const { type, isRedirected, senderName } = data;
        if (type === 'redirect-user-to-personal-room-confirmation' && isRedirected && senderName) {
            showConfirmationDialog(isRedirected, senderName);
        }
    };

    return handleRedirectUserToPersonalRoomConfirmation;
};

const useUserCurrentStatusHandler = () => {
    const localProfile = useLocalProfile();
    const setOtherUsersStatusMap = useSetOtherUsersStatusMap();

    const handleUserCurrentStatus = async (data: any) => {
        try {
            // Normalize data to array of IStatusSnapshot
            const statusUpdates = Array.isArray(data) ? data : [data];

            // Filter out current user and build map for persistence
            const otherUsersUpdates = statusUpdates.filter((item: any) => item?.userId !== localProfile?.uid);

            if (otherUsersUpdates.length === 0) return;

            // Persist each to IndexedDB
            for (const update of otherUsersUpdates) {
                if (update?.userId && update?.status) {
                    await putStoredStatus({
                        ...update,
                        version: 1
                    });
                }
            }

            // Update in-memory state map - build new map and set it
            const newMap: Record<string, any> = {};
            for (const update of otherUsersUpdates) {
                if (update?.userId) {
                    markSocketStatusUpdate(update.userId);
                    newMap[update.userId] = { ...update, version: 1 };
                }
            }
            setOtherUsersStatusMap((prev) => ({ ...prev, ...newMap }));
        } catch (err) {
            console.error('❌ Error handling user current status:', err);
        }
    };

    return handleUserCurrentStatus;
};

// const useUserProfileHandler = () => {
//     const handleUserProfile = (data: any) => {
//         console.log('🚀 ~ file: users.tsx:164 ~ handleUserProfile ~ data:', data);
//     };

//     return handleUserProfile;
// };

export const useAllHandlers = (): TEventHandler[] => {
    const handleUserListUpdate = useUserListUpdateHandler();
    const handleRoomListUpdate = useRoomListUpdateHandler();
    const handleMessage = useMessageHandler();
    const handleVisitorJoined = useVisitorJoinedHandler();
    const handleVisitorKickedOut = useVisitorKickedOutHandler();
    const handleVisitorLists = useVisitorListHandler();
    const handleUserListForUserPresentAtVisitorEntrance = useUserListForUserPresentAtVisitorEntranceHandler();
    const handleCrossAreaMessageUserLists = useCrossAreaMessageUserListsHandler();
    const handleRoomSessionToken = useRoomSessionTokenHandler();
    const handleMessageAtVisitorEntrance = useMessageAtVisitorEntranceHandler();
    const handleNumberOfUsersAndVisitorsInAreas = useNumberOfUsersAndVisitorsInAreasHandler();
    const handleUsersLocationInAreaForHost = useUsersLocationInAreaForHostHandler();
    const handleReloadPage = useReloadPageHandler();
    const handleRedirectUserToPersonalRoom = useRedirectUserToPersonalRoomHandler();
    const handleRedirectUserToPersonalRoomConfirmation = useRedirectUserToPersonalRoomConfirmationHandler();
    const handleUserCurrentStatus = useUserCurrentStatusHandler();
    // const handleUserProfile = useUserProfileHandler();

    return [
        {
            eventName: SERVER_EMIT_USER_JOINED,
            callback: handleUserListUpdate
        },
        {
            eventName: SERVER_EMIT_USER_JOINED_AN_AREA,
            callback: handleUserListUpdate
        },
        {
            eventName: SERVER_EMIT_USER_LEFT_OR_LOGGED_OUT,
            callback: handleUserListUpdate
        },
        {
            eventName: SERVER_EMIT_ROOM_EVENT,
            callback: handleRoomListUpdate
        },
        {
            eventName: SERVER_EMIT_MESSAGE_EVENT,
            callback: handleMessage
        },
        {
            eventName: SERVER_EMIT_KICK_VISITOR,
            callback: handleVisitorKickedOut
        },
        {
            eventName: SERVER_EMIT_VISITOR_JOINED,
            callback: handleVisitorJoined
        },
        {
            eventName: SERVER_EMIT_USER_AT_VISITOR_ENTRANCE,
            callback: handleVisitorLists
        },
        {
            eventName: SERVER_EMIT_HOST_USER_AT_VISITOR_ENTRANCE,
            callback: handleUserListForUserPresentAtVisitorEntrance
        },
        {
            eventName: SERVER_EMIT_CROSS_AREA_MESSAGE_USER_LIST,
            callback: handleCrossAreaMessageUserLists
        },
        {
            eventName: SERVER_EMIT_ROOM_SESSION_TOKEN,
            callback: handleRoomSessionToken
        },
        {
            eventName: SERVER_EMIT_MESSAGE_AT_VISITOR_ENTRANCE,
            callback: handleMessageAtVisitorEntrance
        },
        {
            eventName: SERVER_EMIT_NUMBER_OF_USERS_AND_VISITORS_IN_AREAS,
            callback: handleNumberOfUsersAndVisitorsInAreas
        },
        {
            eventName: SERVER_EMIT_USERS_LOCATION_IN_AREA_FOR_HOST,
            callback: handleUsersLocationInAreaForHost
        },
        {
            eventName: SERVER_EMIT_RELOAD_PAGE,
            callback: handleReloadPage
        },
        {
            eventName: SERVER_EMIT_REDIRECT_USER_TO_PERSONAL_ROOM,
            callback: handleRedirectUserToPersonalRoom
        },
        {
            eventName: SERVER_EMIT_REDIRECT_USER_TO_PERSONAL_ROOM_CONFIRMATION,
            callback: handleRedirectUserToPersonalRoomConfirmation
        },
        {
            eventName: SERVER_EMIT_USER_CURRENT_STATUS,
            callback: handleUserCurrentStatus
        }
        // {
        //     eventName: SERVER_EMIT_USER_PROFILE,
        //     callback: handleUserProfile
        // }
    ];
};
